import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Pagination,
    Popover,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Badge,
} from '@mui/material';
import {
    FilterListRounded,
    ViewAgendaRounded,
    ViewListRounded,
    CloseRounded,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Rodapé de paginação padrão das listas.
 *
 * @param {{
 *   page?: number,
 *   pages?: number,
 *   total?: number,
 *   pageSize?: number,
 *   limit?: number,
 *   pageSizeOptions?: number[],
 *   onChange?: (event: unknown, page: number) => void,
 *   onPageSizeChange?: (pageSize: number) => void,
 *   label?: string,
 *   itemLabel?: string,
 * }} pagination
 */
function ListPaginationBar({ pagination }) {
    const page = Math.max(1, Number(pagination.page || 1));
    const pages = Math.max(1, Number(pagination.pages || 1));
    const total = Number(pagination.total);
    const hasTotal = Number.isFinite(total) && total >= 0;
    const pageSize = Math.max(1, Number(pagination.pageSize || pagination.limit || 10));
    const pageSizeOptions = pagination.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
    const resolvedPageSizeOptions = pageSizeOptions.includes(pageSize)
        ? pageSizeOptions
        : [...pageSizeOptions, pageSize].sort((a, b) => a - b);
    const showPageSize = typeof pagination.onPageSizeChange === 'function';

    const from = !hasTotal || total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = !hasTotal || total === 0 ? 0 : Math.min(page * pageSize, total);

    const rangeLabel =
        pagination.label ||
        (hasTotal
            ? total === 0
                ? `Nenhum${pagination.itemLabel ? ` ${pagination.itemLabel}` : ' registro'}`
                : `Mostrando ${from}–${to} de ${total.toLocaleString('pt-BR')}${
                      pagination.itemLabel ? ` ${pagination.itemLabel}` : ''
                  }`
            : null);

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            gap={{ xs: 1.5, md: 2 }}
            px={2}
            py={1.5}
        >
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
                {rangeLabel || '—'}
            </Typography>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                gap={1.5}
                flexWrap="wrap"
                sx={{ flex: 1 }}
            >
                {showPageSize ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            Por página
                        </Typography>
                        <TextField
                            select
                            size="small"
                            value={pageSize}
                            onChange={event => pagination.onPageSizeChange(Number(event.target.value))}
                            inputProps={{ 'aria-label': 'itens por página' }}
                            sx={{
                                minWidth: 72,
                                '& .MuiInputBase-root': { height: 32 },
                                '& .MuiSelect-select': { py: 0.5 },
                            }}
                        >
                            {resolvedPageSizeOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                ) : null}

                <Pagination
                    page={page}
                    count={pages}
                    onChange={pagination.onChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    disabled={pages <= 1}
                    sx={{
                        alignSelf: { xs: 'center', sm: 'center' },
                        '& .MuiPagination-ul': { flexWrap: 'nowrap' },
                    }}
                />
            </Stack>
        </Stack>
    );
}

/**
 * Shell padrão para páginas de lista do admin.
 *
 * Layout:
 *  Title + subtitle
 *  Card toolbar: counts (esquerda) | ações + filtros + view mode (direita)
 *  Conteúdo (lista ou cards)
 *  Paginação: intervalo | por página | controles
 */
export default function ListPageLayout({
    title,
    subtitle,
    counts = [],
    actions = null,
    /** Conteúdo do painel de filtros (Popover). Se omitido e onFiltersClick for passado, só dispara o clique. */
    filterPanel = null,
    onFiltersClick = null,
    filterActive = false,
    onClearFilters = null,
    showViewToggle = true,
    viewMode: controlledViewMode,
    onViewModeChange,
    viewStorageKey = null,
    defaultViewMode = 'list',
    loading = false,
    /**
     * { page, pages, onChange, total?, pageSize?, pageSizeOptions?, onPageSizeChange?, label?, itemLabel? }
     */
    pagination = null,
    empty = null,
    isEmpty = false,
    /** Conteúdo entre o título e o card da lista (ex.: resumo da conta). */
    intro = null,
    children,
    maxWidth = 'xl',
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [internalViewMode, setInternalViewMode] = useState(() => {
        if (controlledViewMode) return controlledViewMode;
        if (viewStorageKey && typeof window !== 'undefined') {
            const stored = window.localStorage.getItem(viewStorageKey);
            if (stored === 'list' || stored === 'cards') return stored;
        }
        return defaultViewMode;
    });

    const viewMode = controlledViewMode ?? internalViewMode;

    useEffect(() => {
        if (controlledViewMode) return;
        if (viewStorageKey && typeof window !== 'undefined') {
            window.localStorage.setItem(viewStorageKey, viewMode);
        }
    }, [viewMode, viewStorageKey, controlledViewMode]);

    const setViewMode = next => {
        if (!next) return;
        if (onViewModeChange) onViewModeChange(next);
        else setInternalViewMode(next);
    };

    const handleFiltersClick = event => {
        if (onFiltersClick) {
            onFiltersClick(event);
            return;
        }
        if (filterPanel) setAnchorEl(event.currentTarget);
    };

    const filtersOpen = Boolean(anchorEl);
    const hasFilterUi = Boolean(filterPanel || onFiltersClick);

    const content =
        typeof children === 'function' ? children({ viewMode }) : children;

    return (
        <Container maxWidth={maxWidth}>
            <Stack spacing={0.5} pb={2}>
                <Typography variant="h2">{title}</Typography>
                {subtitle ? (
                    <Typography color="text.secondary" variant="body2">
                        {subtitle}
                    </Typography>
                ) : null}
            </Stack>

            {intro}

            <Card variant="outlined">
                {loading ? <LinearProgress /> : null}

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    flexWrap="wrap"
                    px={2}
                    py={1.5}
                >
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        {counts.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                —
                            </Typography>
                        ) : (
                            counts.map((c, i) => (
                                <Chip
                                    key={c.key || c.label || i}
                                    size="small"
                                    color={c.color || 'default'}
                                    variant={c.selected ? 'filled' : (c.variant || 'outlined')}
                                    label={`${c.value ?? 0}${c.label ? ` ${c.label}` : ''}`}
                                    onClick={c.onClick}
                                    clickable={Boolean(c.onClick)}
                                    sx={{ fontWeight: 500 }}
                                />
                            ))
                        )}
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        {actions}
                        {hasFilterUi ? (
                            <Badge color="primary" variant="dot" invisible={!filterActive} overlap="circular">
                                <Button
                                    size="small"
                                    variant={filterActive ? 'contained' : 'outlined'}
                                    startIcon={<FilterListRounded />}
                                    onClick={handleFiltersClick}
                                >
                                    Filtros
                                </Button>
                            </Badge>
                        ) : null}
                        {filterActive && onClearFilters ? (
                            <Button size="small" variant="text" onClick={onClearFilters}>
                                Limpar
                            </Button>
                        ) : null}
                        {showViewToggle ? (
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={viewMode}
                                onChange={(_, next) => setViewMode(next)}
                                aria-label="modo de visualização"
                            >
                                <ToggleButton value="list" aria-label="lista">
                                    <Tooltip title="Lista">
                                        <ViewListRounded fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="cards" aria-label="cards">
                                    <Tooltip title="Cards">
                                        <ViewAgendaRounded fontSize="small" />
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        ) : null}
                    </Stack>
                </Stack>

                <Divider />

                {isEmpty ? (
                    <Box px={2} py={6}>
                        {empty || (
                            <Typography color="text.secondary" textAlign="center">
                                Nenhum item encontrado.
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box>{content}</Box>
                )}

                {pagination && !isEmpty ? (
                    <>
                        <Divider />
                        <ListPaginationBar pagination={pagination} />
                    </>
                ) : null}
            </Card>

            {filterPanel ? (
                <Popover
                    open={filtersOpen}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: { p: 2, width: { xs: 'calc(100vw - 32px)', sm: 360 }, maxWidth: 420 },
                        },
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Filtros
                        </Typography>
                        <IconButton size="small" onClick={() => setAnchorEl(null)} aria-label="fechar filtros">
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    </Stack>
                    {typeof filterPanel === 'function'
                        ? filterPanel({ close: () => setAnchorEl(null) })
                        : filterPanel}
                </Popover>
            ) : null}
        </Container>
    );
}
